import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toApiError } from "../../../lib/http/errors";
import { useFieldApi } from "../../../hooks/useFieldApi";
import { Button, ButtonLink } from "../../../components/ui/Button";
import { FeBanner } from "../../../components/FeBanner";
import { ErrorBanner, Loading, ScreenRoot, ToastHost, useToasts } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import type { TopBarSpec } from "../../../components/TopBar";
import { useT } from "../../../i18n";
import {
  RECENT_ROOM,
  assessPath,
  boardPath,
  playgroundPath,
  relayPath,
  roomConsolePath,
  videoPath,
} from "../../../constants/paths";
import { useSession } from "../../../store/session/SessionProvider";
import { useRoomApi } from "../api/roomApi";
import { useBoardConnection } from "../hooks/useBoardConnection";
import { ownerOf } from "../lib/connection";
import { RoomScene } from "./RoomScreen";
import { CmdRejected, useConsoleApi } from "../api/consoleApi";
import type { ConsoleApi } from "../api/consoleApi";
import type { ConsoleCmd, ConsoleTab, TeacherConsole } from "../types";
import { AssessPanel } from "../../assess";
import { AssessRejected, useAssessApi } from "../../assess";
import { RelayPanel } from "./RelayPanel";
import { useRelayApi } from "../api/relayApi";
import { SandboxPanel } from "./SandboxPanel";
import { ScriptPanel } from "./ScriptPanel";
import { TeamsPanel } from "./TeamsPanel";
import { EndButton, RoomControl, StudentTiles } from "./ProgressPanel";
import { PrepNothing, PrepPanel, RELAY_MODE } from "./PrepPanel";
import { fmtLeft, stepOf } from "../lib/consoleView";
import type { ConsoleStep } from "../lib/consoleView";
export { TIMER_CHOICES, fmtLeft, sortStudents } from "../lib/consoleView";
import { useVideoApi } from "../../video";
import { VideoTab } from "../../video";
import { ExchangePanel } from "../../exchange";
import { useExchangeApi } from "../../exchange";
import "../../../styles/teacher.css";
import "../../../styles/console.css";
import "../../../styles/closing.css";

/** 콘솔은 1초마다 console 을 다시 읽는다(학생 반영 ≤1초 · 계약 §4) */
export const CONSOLE_POLL_MS = 1000;

/** 탭 → 화면 ID(SCR) */
const SCREEN_ID: Record<ConsoleTab, string> = {
  room: "FS-11",
  teams: "FS-11-03",
  script: "FS-13-01",
  sandbox: "FS-07-01",
  relay: "FS-08-01",
  assess: "FS-11-04",
  video: "FS-14-02",
  exchange: "FS-11-08",
};
const TAB_TITLE: Record<ConsoleTab, string> = {
  room: "teacher.room.title",
  teams: "teacher.room.tabTeams",
  script: "teacher.room.tabScript",
  sandbox: "teacher.room.tabSandbox",
  relay: "teacher.room.tabRelay",
  assess: "teacher.room.tabNotes",
  video: "teacher.room.tabVideo",
  exchange: "teacher.room.tabExchange",
};

/** 패널이 상단 바를 정하지 않을 때(진행 화면의 배치 · 구간 도구) */
const NO_TOP = () => {};

/** 1초마다 읽기 — 겹쳐 부르지 않는다(느린 응답이 쌓이지 않게) */
export function useConsolePoll(api: ConsoleApi | null) {
  const [data, setData] = useState<TeacherConsole | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef(false);
  const refresh = useCallback(async () => {
    if (!api || inflight.current) return;
    inflight.current = true;
    try {
      setData(await api.console());
      setError(null);
    } catch (e) {
      setError(toApiError(e).code);
    } finally {
      inflight.current = false;
    }
  }, [api]);
  useEffect(() => {
    if (!api) return;
    void refresh();
    const h = window.setInterval(() => void refresh(), CONSOLE_POLL_MS);
    return () => window.clearInterval(h);
  }, [api, refresh]);
  return { data, error, refresh, setData };
}

/**
 * FS-11 교사 룸 콘솔(PH2 · SU/공통 · SCR v0.6 sfs-11 · sfs-11-03) — /teacher/room/:roomId(01 룸 제어 + 02 학생 목록·개입)
 * · /teacher/room/:roomId/teams(03 매칭·팀 편성).
 *  - 좌측 짙은 탭: 룸 제어·학생 · 매칭·팀 편성 · 마무리 스크립트(FS-13-01 · P2-S5) · 자유 놀이터 배치(FS-07 교사 · P2-S5)
 *    · 메모·성취(FS-11-04 · P2-S6 — 룸이 닫혀 있어도 연다) · 놀이터 설정 묶음(PH1 탭 링크) · 칠판 뷰 열기 →(새 창 /board/:roomId · P2-S5)
 *  - P2-S5 ★ = 칠판 하이라이트(그 학생 기계를 칠판 카메라가 따라감 · 한 번 더 누르면 끔)
 *  - 상단 바 '👩‍🏫 3학년 2반 · 마무리 놀이터' + 칩(룸 진행 중 · 밀어내기 · 남은 4:12 · 연결 28/30)
 *  - 모든 조작 ≤2클릭(모드·타이머는 펼쳐 고르기) · 룸 종료만 확인 한 번 더 · 명령은 서버가 역할을 다시 확인하고 감사 로그에 남긴다
 *  - 학생 목록: 정렬은 이름/연결 상태만 · 점수·순위 열 없음(DR-01) · 분리 = 관전 전용(학생에게는 중립 문구만 — SR-SEC-03)
 *  - P3-S1 릴레이(FS-08-01 · SCR v0.7 검토 v07-03) — 탭은 릴레이 API 가 enabled(Phase 3 플래그)일 때만 보인다
 *  - P3-S4 교류 룸(SCR v0.7 검토 §4): 교류 룸 초대·수락 탭(FS-11-08 · 룸이 없어도 연다) · 공동 호스트 변형(FS-11-01 —
 *    상단 '두 반 함께 · 3-2 + 3-4 · 연결 n/60' · 학생 목록 반별 필터 칩 · 반 칸 · 자기 반이 아닌 학생의 개별 조치는 끄고 이유 · Q-5)
 *    · 교류 룸이면 릴레이 탭·모드 없음(Q-3) · 정원이 다 차면 FE-301(정원 수만 다르다 — FS-C1-03 변형)
 *  - 💡 원리 보기(SR-PHY-07 · 2026-09-28): 학생 행 💡 로 기계를 고르면 목록 아래 원리 보기 칸이 열린다 — 칠판 조인 토큰으로
 *    룸에 붙은 읽기 전용 3D(학생 룸 화면과 같은 RoomScene · 토글 · 범례 · 수치 라벨)가 그 기계를 따라가고 `pr {on, machine}` 로
 *    서버 값(`pp`)을 받는다. 다른 행 💡 = 기계 바꾸기 · 같은 행 또는 칸의 💡 = 닫기(연결도 닫는다). 켤 수 있는 행은
 *    다른 개별 조치(리셋 · 분리 · ★)와 같다 — 기계 있음 · 미접속 아님 · 예열 아님 · 교류 룸이면 우리 반 학생만(Q-5).
 */
export function TeacherConsoleScreen({ tab }: { tab: ConsoleTab }) {
  const t = useT();
  const { roomId = "" } = useParams();
  const navigate = useNavigate();
  const session = useSession();
  const courseId = session.status === "ready" ? session.session.courseId : "";
  const api = useConsoleApi();
  const roomApi = useRoomApi();
  const field = useFieldApi();
  const { data, error, refresh, setData } = useConsolePoll(api);
  const assessApi = useAssessApi();
  const relayApi = useRelayApi();
  const relayOn = useRelayEnabled(relayApi);
  const videoApi = useVideoApi();
  const exchangeApi = useExchangeApi();
  const toasts = useToasts();
  const [course, setCourse] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // 💡 원리 보기로 고른 기계(null = 닫힘) — 그 학생이 기계를 잃으면(미접속 · 명부에서 빠짐) 닫는다
  const [principle, setPrinciple] = useState<number | null>(null);
  // 2 진행의 모드 도구(보완 v0.1 2.1 · 2.2) — 🧸 배치판 · 🔗 구간 진행을 진행 화면 아래에 펼친다
  const [tool, setTool] = useState<"sandbox" | "relay" | null>(null);

  useEffect(() => {
    if (!field) return;
    let alive = true;
    field
      .teacherOverview()
      .then((d) => alive && setCourse(d?.courseLabel ?? null))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [field]);

  const room = data?.room && data.room.roomId === roomId ? data.room : null;
  const students = data?.students ?? [];
  const principleKid =
    principle !== null
      ? (students.find((s) => s.machine === principle && s.connection !== "offline") ?? null)
      : null;
  useEffect(() => {
    if (principle !== null && data && !principleKid) setPrinciple(null);
  }, [principle, data, principleKid]);
  const works = new Map(students.flatMap((s) => (s.work ? [[s.pid, s.work] as const] : [])));
  const online = students.filter((s) => s.connection === "online").length;
  // P3-S4 교류 룸 — 코스 두 개(호스트 먼저) · 연결은 정원 대비(n/60)
  const exchange = !!room && data?.kind === "exchange";
  const courses = exchange ? (data?.courses ?? []) : [];
  const modeName = room ? t(`teacher.room.modeName.${room.modeId}`) : "";
  // 교류 룸이면 릴레이 없음(Q-3) · 릴레이는 Phase 3 플래그가 켜졌을 때만
  const modes = (data?.modes ?? []).filter(
    (m) => (relayOn && !exchange) || (m.kind !== "relay" && m.id !== RELAY_MODE),
  );
  const courseLabel = course ?? courseId;

  const step = stepOf(tab);
  const chips = [];
  if ((step === 2 || step === 3) && room) {
    chips.push({ label: t(`teacher.room.chip.${room.state}`), testId: "room-state-chip" });
    chips.push({
      label: room.timer
        ? t("teacher.room.chipModeTimer", { mode: modeName, left: fmtLeft(room.timer.endsInS) })
        : modeName,
      testId: "room-mode-chip",
    });
  }
  if (step === 1 && room) {
    const teams = new Set(
      students.flatMap((s) => (s.activity?.kind === "team" ? [s.activity.team] : [])),
    ).size;
    const pairs = Math.floor(students.filter((s) => s.activity?.kind === "match").length / 2);
    chips.push({
      label:
        room.modeId === "carry-together"
          ? t("teacher.room.chipTeams", { mode: modeName, n: teams })
          : room.modeId === "pushout-ring"
            ? t("teacher.room.chipPairs", { mode: modeName, n: pairs })
            : modeName,
      testId: "room-mode-chip",
    });
  }
  if (data) {
    chips.push({
      label: t("teacher.room.chipOnline", {
        n: online,
        total: exchange && room ? room.capacity : students.length,
      }),
      testId: "room-online-chip",
    });
  }
  // 스크립트 · 배치 · 메모·성취 탭은 패널이 상단 바를 정한다(단계 n/전체 · 물건 n/50 · 입력 n/m)
  const [panelTop, setPanelTop] = useState<TopBarSpec | null>(null);
  useEffect(() => setPanelTop(null), [tab]);
  const panelIcon =
    tab === "script"
      ? "📋"
      : tab === "sandbox"
        ? "🧸"
        : tab === "relay"
          ? "🔗"
          : tab === "video"
            ? "🎬"
            : tab === "exchange"
              ? "🤝"
              : "📝";
  // 메모·성취 · 영상 · 교류 초대 탭은 룸이 닫혀도 연다(룸 상태 오류 · FE-303 배너를 띄우지 않는다)
  const roomless = tab === "assess" || tab === "video" || tab === "exchange";
  useTopBar(
    panelTop ??
      (roomless
        ? { icon: panelIcon, title: t(TAB_TITLE[tab]) }
        : {
            icon: "👩‍🏫",
            title: exchange
              ? t("teacher.room.exchange.topTitle", {
                  courses: courses.map((c) => c.label).join(" + "),
                })
              : t("teacher.room.topTitleStep", {
                  course: courseLabel,
                  step: t(`teacher.room.step${step ?? 2}`),
                }),
            chips,
          }),
  );

  /** 실패 알림 — 교사·운영 토스트는 문의 코드를 붙인다 */
  const report = useCallback(
    (e: unknown) => {
      if (e instanceof CmdRejected || e instanceof AssessRejected)
        toasts.push("error", t("teacher.room.cmdRejected", { field: e.field }), "HTTP-422");
      else
        toasts.push(
          "error",
          t(
            tab === "assess"
              ? "assess.saveFailed"
              : tab === "video"
                ? "video.downloadFailed"
                : tab === "exchange"
                  ? "exchange.failed"
                  : "teacher.room.cmdFailed",
          ),
          toApiError(e).code,
        );
    },
    [toasts, t, tab],
  );

  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    try {
      await fn();
      await refresh();
    } catch (e) {
      report(e);
    } finally {
      setBusy(null);
    }
  };
  const cmd = (key: string, c: ConsoleCmd) =>
    run(key, async () => {
      const r = await api!.cmd(c);
      if (r) setData((d) => (d ? { ...d, room: r } : d));
    });
  const setMode = (modeId: string) =>
    run("mode", async () => {
      await roomApi!.setMode(modeId);
    });
  const endRoom = () =>
    run("end", async () => {
      await roomApi!.close();
      navigate("/teacher", { replace: true });
    });

  return (
    <ScreenRoot
      id={SCREEN_ID[tab]}
      title={t(TAB_TITLE[tab])}
      hideTitle
      className="pf-console pf-rc"
      focusKey={tab}
    >
      <div className="pf-console__frame">
        <ConsoleNav roomId={roomId} courseId={courseId} tab={tab} step={step} />
        <div className="pf-console__body" data-tab-panel={tab}>
          {error && !roomless && (
            <ErrorBanner
              cause={t("teacher.room.loadFailed")}
              action={t("teacher.common.retryLater")}
              code={error}
            />
          )}
          {room?.server === "down" && !roomless && (
            // 보완 v0.1 2.5 — 끊겼을 때만 빨간 칩 + '↻ 다시 연결'(세 단계 어디서든 같은 자리 · 큰 버튼은 그대로)
            <div className="pf-down" role="alert" data-testid="rc-down" data-fe-code="FE-303">
              <span className="pf-chip-static pf-chip-static--err">
                {t("teacher.room.serverDown")}
              </span>
              <Button
                variant="ghost"
                className="pf-btn--sm"
                data-testid="rc-reconnect"
                onClick={() => void run("reconnect", () => roomApi!.open())}
              >
                {t("teacher.home.reconnectRoom")}
              </Button>
            </div>
          )}
          {room && !roomless && online >= room.capacity && (
            // FS-C1-03 변형 — 문구는 같고 정원 수만(교류 룸 60 · 반 룸 30)
            <FeBanner code="FE-301" audience="teacher" actions={[]}>
              <span data-testid="room-full-note">
                {t("teacher.room.fullNote", { n: room.capacity })}
              </span>
            </FeBanner>
          )}
          {tab === "exchange" ? (
            // P3-S4 FS-11-08 교류 룸 초대·수락 — 룸이 없어도 연다 · 열면 그 룸 콘솔로
            exchangeApi && (
              <ExchangePanel
                api={exchangeApi}
                courseLabel={courseLabel}
                onTop={setPanelTop}
                onError={report}
                onDone={(text) => toasts.push("info", text)}
                onOpened={(id) => navigate(roomConsolePath(id))}
              />
            )
          ) : tab === "assess" ? (
            // 메모·성취는 룸이 닫힌 뒤에도 입력한다(기본 활동 = 열린 룸의 활동, 없으면 가장 최근)
            assessApi && (
              <AssessPanel
                api={assessApi}
                courseLabel={courseLabel}
                works={works}
                onTop={setPanelTop}
                onError={report}
                onSaved={(text) => toasts.push("info", text)}
              />
            )
          ) : tab === "video" ? (
            // P3-S3 FS-14-02 영상 — 렌더는 룸이 닫힌 뒤에도 끝난다(SCR v0.7 검토 4-4) · 'recent' 면 코스 전체
            videoApi && (
              <VideoTab
                api={videoApi}
                roomId={roomId && roomId !== RECENT_ROOM ? roomId : null}
                courseLabel={courseLabel}
                onTop={setPanelTop}
                onError={report}
                onDone={(text) => toasts.push("info", text)}
              />
            )
          ) : !data ? (
            !error && <Loading />
          ) : !room ? (
            <section className="pf-pane pf-pane--hero" data-testid="console-no-room">
              <h2 className="pf-h3">{t("teacher.room.noRoom")}</h2>
              <p className="pf-muted">{t("teacher.room.noRoomNext")}</p>
              <div className="pf-row">
                <ButtonLink to="/teacher" variant="ghost" className="pf-btn--sm">
                  {t("teacher.room.backHome")}
                </ButtonLink>
              </div>
            </section>
          ) : step === 1 ? (
            <PrepPanel
              room={room}
              modes={modes}
              picked={tab === "relay" ? RELAY_MODE : tab === "sandbox" ? "sandbox" : room.modeId}
              students={students}
              busy={busy}
              progressTo={roomConsolePath(roomId)}
              onMode={(id) => {
                // 구간판(/relay) · 배치판(/sandbox) 주소에서 다른 카드를 고르면 1 준비 기본 주소로 돌아온다 —
                // 그대로 두면 준비판이 계속 구간판 · 배치판으로 남는다. 모드가 이미 같으면(예: 로비) 바꾸지 않는다
                if (tab !== "teams") navigate(`${roomConsolePath(roomId)}/teams`);
                if (id !== room.modeId) void setMode(id);
              }}
              onRelay={() => navigate(relayPath(roomId))}
              onCmd={(key, c) => void cmd(key, c)}
              onStart={() =>
                void run("resume", async () => {
                  await api!.cmd({ kind: "resume" });
                  navigate(roomConsolePath(roomId));
                })
              }
            >
              {tab === "relay" || (tab === "teams" && room.modeId === RELAY_MODE) ? (
                relayApi && (
                  <RelayPanel
                    room={room}
                    api={relayApi}
                    courseLabel={courseLabel}
                    onTop={setPanelTop}
                    onError={report}
                    onDone={(text) => toasts.push("info", text)}
                  />
                )
              ) : tab === "sandbox" || room.modeId === "sandbox" ? (
                <SandboxPanel
                  room={room}
                  api={api!}
                  onTop={setPanelTop}
                  onError={report}
                  onMode={(id) => void setMode(id)}
                  onDone={(text) => toasts.push("info", text)}
                />
              ) : room.modeId === "pushout-ring" || room.modeId === "carry-together" ? (
                <TeamsPanel
                  room={room}
                  students={students}
                  api={api!}
                  onApplied={refresh}
                  onError={report}
                  onDone={(text) => toasts.push("info", text)}
                />
              ) : (
                <PrepNothing />
              )}
            </PrepPanel>
          ) : step === 2 ? (
            <>
              <RoomControl
                room={room}
                modes={modes}
                busy={busy}
                onCmd={(key, c) => void cmd(key, c)}
                onMode={(id) => void setMode(id)}
                onEnd={() => void endRoom()}
                tools={
                  room.modeId === "sandbox" ? (
                    <Button
                      variant={tool === "sandbox" ? "green" : "ghost"}
                      className="pf-btn--sm"
                      aria-pressed={tool === "sandbox"}
                      data-testid="rc-tool-sandbox"
                      onClick={() => setTool((x) => (x === "sandbox" ? null : "sandbox"))}
                    >
                      {t("teacher.room.toolSandbox")}
                    </Button>
                  ) : room.modeId === RELAY_MODE && relayApi ? (
                    <Button
                      variant={tool === "relay" ? "green" : "ghost"}
                      className="pf-btn--sm"
                      aria-pressed={tool === "relay"}
                      data-testid="rc-tool-relay"
                      onClick={() => setTool((x) => (x === "relay" ? null : "relay"))}
                    >
                      {t("teacher.room.toolRelay")}
                    </Button>
                  ) : null
                }
              />
              {tool === "sandbox" && room.modeId === "sandbox" && (
                <SandboxPanel
                  room={room}
                  api={api!}
                  onTop={NO_TOP}
                  onError={report}
                  onMode={(id) => void setMode(id)}
                  onDone={(text) => toasts.push("info", text)}
                />
              )}
              {tool === "relay" && room.modeId === RELAY_MODE && relayApi && (
                <RelayPanel
                  room={room}
                  api={relayApi}
                  courseLabel={courseLabel}
                  onTop={NO_TOP}
                  onError={report}
                  onDone={(text) => toasts.push("info", text)}
                />
              )}
              <StudentTiles
                room={room}
                students={students}
                busy={busy}
                onCmd={(key, c) => void cmd(key, c)}
                roster={data.roster}
                courses={courses}
                principle={principleKid ? principle : null}
                onPrinciple={(m) => setPrinciple((cur) => (cur === m ? null : m))}
              />
              {principleKid && api && (
                <ConsolePrinciple
                  api={api}
                  machine={principleKid.machine!}
                  name={principleKid.nickname}
                  onClose={() => setPrinciple(null)}
                />
              )}
            </>
          ) : (
            <>
              <ScriptPanel
                room={room}
                api={api!}
                onTop={setPanelTop}
                onError={report}
                onChanged={refresh}
              />
              <WrapRecords roomId={roomId} busy={busy} onEnd={() => void endRoom()} />
            </>
          )}
        </div>
      </div>
      <ToastHost items={toasts.items} onDismiss={toasts.dismiss} pico={false} />
    </ScreenRoot>
  );
}

/** 릴레이 탭을 보일까 — GET …/relay 의 enabled(Phase 3 플래그 field.phase3.relay) · 못 읽으면 숨긴다 */
function useRelayEnabled(api: ReturnType<typeof useRelayApi>): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!api) return;
    let alive = true;
    api
      .get()
      .then((r) => alive && setOn(r.enabled))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [api]);
  return on;
}

/**
 * 왼쪽 메뉴(화면 설계 설명서 2.2 ② · ③) — 수업 순서 1 준비 · 2 진행 · 3 마무리(지금 단계 초록) + ⚙ 설정(접힘 — 메모 · 성취 · 영상 ·
 * 교류 룸 초대 · 놀이터 설정 묶음) + 칠판 뷰 열기(세 단계 어디서든 같은 자리). 자유 놀이터 배치 · 릴레이 구간은 1 준비의 모드 카드로 간다.
 */
function ConsoleNav({
  roomId,
  courseId,
  tab,
  step,
}: {
  roomId: string;
  courseId: string;
  tab: ConsoleTab;
  step: ConsoleStep | null;
}) {
  const t = useT();
  const base = roomConsolePath(roomId);
  const pg = playgroundPath(courseId);
  const steps: readonly [ConsoleStep, string, string][] = [
    [1, `${base}/teams`, "teams"],
    [2, base, "room"],
    [3, `${base}/script`, "script"],
  ];
  const settings: readonly [ConsoleTab, string, string][] = [
    ["assess", assessPath(roomId), "teacher.room.tabNotes"],
    ["video", videoPath(roomId), "teacher.room.tabVideo"],
    ["exchange", `${base}/exchange`, "teacher.room.tabExchange"],
  ];
  return (
    <nav className="pf-console__side" aria-label={t("teacher.room.nav")}>
      <ol className="pf-console__tabs pf-stepnav" data-testid="console-tabs">
        {steps.map(([n, to, id]) => (
          <li key={n}>
            <Link
              to={to}
              className={`pf-console__tab pf-stepnav__tab${step === n ? " pf-console__tab--on" : ""}`}
              aria-current={step === n ? "page" : undefined}
              data-tab={id}
              data-step={n}
            >
              {t(`teacher.room.step${n}`)}
            </Link>
          </li>
        ))}
      </ol>
      <details className="pf-console__more" open={step === null} data-testid="console-settings">
        <summary className="pf-console__tab">{t("teacher.room.settings")}</summary>
        <ul className="pf-console__tabs" data-testid="console-ph1-tabs">
          {settings.map(([id, to, key]) => (
            <li key={id}>
              <Link
                to={to}
                className={`pf-console__tab${tab === id ? " pf-console__tab--on" : ""}`}
                aria-current={tab === id ? "page" : undefined}
                data-tab={id}
              >
                {t(key)}
              </Link>
            </li>
          ))}
          {(
            [
              ["settings", "teacher.playground.tabSettings"],
              ["reactions", "teacher.playground.tabReactions"],
              ["works", "teacher.playground.tabWorks"],
            ] as const
          ).map(([id, key]) => (
            <li key={id}>
              <Link to={`${pg}?tab=${id}`} className="pf-console__tab" data-tab={id}>
                {t(key)}
              </Link>
            </li>
          ))}
        </ul>
      </details>
      {/* FS-12 칠판 뷰 — 새 창(같은 탭 세션이 따라가도록 opener 를 남긴다 · 교실 대형 화면으로 옮겨 전체 화면) */}
      <a
        href={boardPath(roomId)}
        target={`pf-board-${roomId}`}
        className="pf-console__tab pf-console__home"
        data-testid="open-board"
        title={t("teacher.room.boardHint")}
        onClick={(e) => {
          const w = window.open(
            boardPath(roomId),
            `pf-board-${roomId}`,
            "popup,width=1280,height=720",
          );
          if (w) e.preventDefault();
        }}
      >
        {t("teacher.room.board")}
      </a>
    </nav>
  );
}

/**
 * 3 마무리 아래(설명서 2.5 ⑤~⑦) — 남은 기록을 같은 화면에서 잇는다: 메모 · 성취 → 확인하러 가기 · 공동 작품 영상(룸이 끝난 뒤에도 열림) · ■ 룸 종료(확인 한 번 더).
 */
function WrapRecords({
  roomId,
  busy,
  onEnd,
}: {
  roomId: string;
  busy: string | null;
  onEnd: () => void;
}) {
  const t = useT();
  const [confirming, setConfirming] = useState(false);
  return (
    <section className="pf-pane pf-wrap" aria-labelledby="wrap-title" data-testid="wrap-records">
      <h2 id="wrap-title" className="pf-h3">
        {t("teacher.room.wrap.title")}
      </h2>
      <div className="pf-wrap__cards">
        <div className="pf-wrap__card">
          <b>{t("teacher.room.tabNotes")}</b>
          <p className="pf-muted">{t("teacher.room.wrap.notesHint")}</p>
          <ButtonLink
            to={assessPath(roomId)}
            variant="ghost"
            className="pf-btn--sm"
            data-testid="wrap-notes"
          >
            {t("teacher.room.wrap.notesGo")}
          </ButtonLink>
        </div>
        <div className="pf-wrap__card">
          <b>{t("teacher.room.wrap.videoTitle")}</b>
          <p className="pf-muted">{t("teacher.room.wrap.videoHint")}</p>
          <ButtonLink
            to={videoPath(roomId)}
            variant="ghost"
            className="pf-btn--sm"
            data-testid="wrap-video"
          >
            {t("teacher.room.wrap.videoGo")}
          </ButtonLink>
        </div>
      </div>
      <div className="pf-wrap__end">
        <EndButton
          busy={busy}
          onEnd={onEnd}
          confirming={confirming}
          setConfirming={setConfirming}
          testId="wrap-end"
        />
      </div>
    </section>
  );
}

/**
 * 💡 원리 보기 칸(SR-PHY-07 · FS-11-02 아래) — 칠판 조인 토큰으로 룸에 붙은 읽기 전용 3D. 고른 기계를 따라가고 서버 값만 그린다
 * (다시 시뮬레이션하지 않는다 · 해시 · 리플레이 무관). 칸이 열려 있는 동안만 연결한다.
 */
function ConsolePrinciple({
  api,
  machine,
  name,
  onClose,
}: {
  api: ConsoleApi;
  machine: number;
  name: string;
  onClose: () => void;
}) {
  const t = useT();
  const conn = useBoardConnection(api);
  useEffect(() => {
    conn?.sendPrinciple(true, machine);
  }, [conn, machine]);
  return (
    <section
      className="pf-pane pf-rc__principle"
      aria-labelledby="rc-principle-title"
      data-testid="rc-principle"
      data-machine={machine}
    >
      <h2 id="rc-principle-title" className="pf-h3">
        {t("teacher.room.principleTitle", { name })}
      </h2>
      <p className="pf-muted">{t("teacher.room.principleNote")}</p>
      {conn?.meta ? (
        <RoomScene
          conn={conn}
          palette={null}
          watch={machine}
          principle={{ on: true, numbers: true }}
          principleOwner={ownerOf(machine)}
          onPrinciple={onClose}
        />
      ) : (
        <Loading />
      )}
    </section>
  );
}
